import React from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  DollarSign, 
  Activity, 
  HeartPulse, 
  TrendingUp, 
  TrendingDown, 
  ArrowRight
} from 'lucide-react';
import { KPIMetric } from '@/lib/index';
import { cn } from '@/lib/utils';

const iconMap: Record<string, React.ElementType> = {
  Users,
  DollarSign,
  Activity,
  HeartPulse,
};

interface KPICardProps {
  metric: KPIMetric;
  index: number;
}

export function KPICard({ metric, index }: KPICardProps) {
  const Icon = iconMap[metric.icon] || Activity;
  const isPositive = metric.trend === 'up';
  const isNeutral = metric.trend === 'neutral';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        duration: 0.4, 
        delay: index * 0.1, 
        type: 'spring', 
        stiffness: 260, 
        damping: 20 
      }}
      whileHover={{ 
        y: -8, 
        scale: 1.02,
        rotateX: 2,
        rotateY: 2,
        transition: { duration: 0.2 }
      }}
      className="relative group perspective-1000"
    >
      {/* 3D Background Layer */}
      <div 
        className="absolute -inset-0.5 rounded-2xl opacity-20 group-hover:opacity-40 transition duration-500 blur-sm"
        style={{ backgroundColor: metric.color || 'var(--primary)' }}
      />
      
      <div className="relative h-full bg-card border border-border/50 rounded-2xl p-6 shadow-sm overflow-hidden flex flex-col justify-between">
        {/* Decorative Grid Pattern Overlay */}
        <div className="absolute top-0 right-0 w-32 h-32 opacity-5 pointer-events-none">
          <svg width="100%" height="100%" viewBox="0 0 100 100">
            <pattern id={`grid-${index}`} width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.5" />
            </pattern>
            <rect width="100%" height="100%" fill={`url(#grid-${index})`} />
          </svg>
        </div>

        <div className="flex items-start justify-between mb-4">
          <div 
            className="p-3 rounded-xl bg-opacity-10 transition-colors duration-300"
            style={{ backgroundColor: `${metric.color}15`, color: metric.color }}
          >
            <Icon className="w-6 h-6" />
          </div>
          
          <div className={cn(
            "flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium",
            isNeutral ? "bg-muted text-muted-foreground" : 
            isPositive ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : 
            "bg-destructive/10 text-destructive"
          )}>
            {isNeutral ? null : isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            <span>{metric.change > 0 ? '+' : ''}{metric.change}%</span>
          </div>
        </div>

        <div>
          <p className="text-sm font-medium text-muted-foreground mb-1">{metric.title}</p>
          <h3 className="text-2xl font-bold tracking-tight font-mono">
            {metric.value}
          </h3>
        </div>

        <div className="mt-4 flex items-center justify-between group/footer">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">
            {metric.description}
          </span>
          <motion.div 
            whileHover={{ x: 4 }}
            className="opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ArrowRight className="w-4 h-4 text-muted-foreground" />
          </motion.div>
        </div>

        {/* Bottom Accent Line */}
        <div 
          className="absolute bottom-0 left-0 h-1 w-full scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"
          style={{ backgroundColor: metric.color || 'var(--primary)' }}
        />
      </div>
    </motion.div>
  );
}

export function KPICards({ metrics }: { metrics: KPIMetric[] }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 w-full">
      {metrics.map((metric, index) => (
        <KPICard 
          key={metric.title} 
          metric={metric} 
          index={index} 
        />
      ))}
    </div>
  );
}
